import VenueBasedTicketBasketRepository from "@app-contexts/dashboard/features/box-office/basket/data/repositories/VenueBasedTicketBasketRepository";
import UserBasedTicketBasketRepository from "@app-contexts/user/features/basket/data/repositories/UserBasedTicketBasketRepository/UserBasedTicketBasketRepository";
import { ITEM_STATUS_OPTIONS } from "@shared/constants";
import { DataProvider } from "@shared/constants/common";
import useMountEffect from "@shared/hooks/useMountEffect/useMountEffect";
import { useStore as store } from "@shared/store/useStore";
import { useCallback, useEffect } from "react";

// Mapping to group DataProviders
const storeToProviderMap: Record<
  "publicBasket" | "venueBasket",
  DataProvider[]
> = {
  publicBasket: [DataProvider.Public, DataProvider.User],
  venueBasket: [DataProvider.Venue],
};

// Mapping to group DataProvider repositories
const providerToRepositoryMap = {
  [DataProvider.Venue]: (venueId?: number) =>
    new VenueBasedTicketBasketRepository({ venueId }),
  [DataProvider.Public]: () => new UserBasedTicketBasketRepository(),
  [DataProvider.User]: () => new UserBasedTicketBasketRepository(),
};

let timerId: NodeJS.Timeout;

/**
 * Clears the basket object from the store by setting it to undefined
 */
const clearLocalBasket = (contextBoundary: DataProvider) => {
  store.getState().setBasket(contextBoundary, undefined);
};

// Function to schedule the action, which is to clear any knowledge of the basket
// on the client
const scheduleAction = (date: string, contextBoundary: DataProvider) => {
  if (timerId) {
    clearTimeout(timerId);
  }

  const now = Date.now();
  const targetDate = new Date(date);
  const timeDifference = targetDate.getTime() - now;

  if (timeDifference > 0) {
    // Cap the timeout to a safe maximum value.
    // For baskets with a very distant expiry_date like holds (+ 1year),
    // This prevents JS from falsely firing setTimeout instantly due to exceeding the 32-bit integer max value.
    // Maximum safe timeout value (slightly under 2^31-1 milliseconds)
    const MAX_TIMEOUT = 2147483000; // ~24.8 days in milliseconds
    const safeTimeout = Math.min(timeDifference, MAX_TIMEOUT);

    timerId = setTimeout(() => {
      clearLocalBasket(contextBoundary);
    }, safeTimeout);
  }
};

/**
 * Component that subscribes to changes to baskets in the store when mounted.
 * This component communicates with the store directly, without any interfaces or facades like usePublicBasket or useVenueBasket.
 * This component should be placed in such a way where only a 1 instance of the subscribers declared in the component is created, such as
 * entry points like _app.tsx or a root layout. In other words, place this component at the highest level of your component tree.
 */
function GlobalBasketObserver() {
  const refreshBasket = useCallback(
    async (
      basketId: number,
      basketUuid: string | undefined,
      dataProvider: DataProvider,
      venueId?: number
    ) => {
      // Determine which repository to use based on DataProvider
      const repositoryFactory = providerToRepositoryMap[dataProvider];
      if (!repositoryFactory) {
        throw new Error(
          `No repository found for DataProvider: ${dataProvider}`
        );
      }

      const basketRepo =
        dataProvider === DataProvider.Venue && venueId
          ? repositoryFactory(venueId)
          : repositoryFactory();

      return basketRepo
        .retrieve(basketId, {
          showToastOnError: false,
          params: {
            uuid: basketUuid,
          },
        })
        .then((basket) => {
          store.getState().setBasket(dataProvider, basket);
          return basket;
        });
    },
    []
  );

  /**
   * On mount, create subscribers for the expiry_date
   */
  useEffect(() => {
    // Create unsubscribers for each unique store key
    // prettier-ignore
    const unsubscribers = Object.entries(storeToProviderMap).map(
      ([variable, providers]) => store.subscribe((state) => ({
          // Select changes to basket expiry_date
          expiry_date: state[variable as "publicBasket" | "venueBasket"]?.expiry_date,
          status: state[variable as "publicBasket" | "venueBasket"]?.status,
        }),
        // Perform action when it changes
        ({ expiry_date, status }) => {

          if (expiry_date && status !== ITEM_STATUS_OPTIONS.ON_HOLD.id) {
            providers.forEach((provider) => {
              scheduleAction(expiry_date, provider);
            });
          }
        }
      )
    );

    return () => {
      if (timerId) {
        clearTimeout(timerId);
      }
      // Unsubscribe from all listeners
      unsubscribers.forEach((unsubscribe) => {
        unsubscribe();
      });
    };
  }, []);

  /**
   * On mount, fetch the basket from the server and clear the client if the status is EXPIRED.
   */
  useMountEffect(() => {
    Object.entries(storeToProviderMap).forEach(([, providers]) => {
      const basket = store.getState().getBasket(providers[0]); // Get the basket using the first provider in the group

      if (basket?.id) {
        // Use the first provider
        const refreshBasketPromise = refreshBasket(
          basket.id,
          basket.uuid,
          providers[0],
          basket.sold_by_venue
        );

        refreshBasketPromise.then((refreshedBasket) => {
          if (refreshedBasket.status === ITEM_STATUS_OPTIONS.EXPIRED.id) {
            // Clear the local basket for each provider in the group
            providers.forEach((provider) => clearLocalBasket(provider));
          } else if (refreshedBasket.expiry_date) {
            scheduleAction(refreshedBasket.expiry_date, providers[0]);
          }
        });
      }
    });
  });

  return null;
}

export default GlobalBasketObserver;
